import { createRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import type { SetRequired } from 'type-fest';
import { z } from 'zod';
import { authenticatedRoute } from '@/authenticated-route';
import { getEventListQueryOptions } from '@/features/form-system/services/use-suspense-query-event-list';
import { displayNameToIdBase } from '@/hooks/use-category-data-transform';
import { beforeLoadRouterGuard } from '@/integrations/tanstack-router/before-load-router-guard';
import { commonQueryParamsSchema } from '@/lib/zod-helper';
import {
  getQueryCategoriesOptions,
  getTypeId,
  SEMATIC_CATEGORY,
} from '@/services/use-query-categories';
import { getQueryFileInfoByIdOptions } from '@/services/use-query-file-info-by-id';
import { PERMISSIONS } from '../../../generated/permissions';
import { SmartFormPatientCardSource } from './components/patient-card-source';
import { FORM_SYS_ROUTES } from './consts/routes';
import { runtimeRowTypeSchema } from './services/types/runtime-row-type-schema';
import { getSuspenseQueryDraftTemplateOptions } from './services/use-query-draft-template';
import { getSuspenseQueryTemplateByIdOptions } from './services/use-query-template-by-id';
import { getUserWidgetsQueryOptions } from './services/use-query-user-widgets';
import type { TemplateImageV1 } from './types/template/image';

const getParentRoute = () => authenticatedRoute;

const FORM_SYS_CHILD_STATIC_DATA = {
  parent: FORM_SYS_ROUTES.index,
  home: FORM_SYS_ROUTES.index,
} as const;

const createFormTemplateRoute = createRoute({
  getParentRoute,
  staticData: FORM_SYS_CHILD_STATIC_DATA,
  beforeLoad: beforeLoadRouterGuard([
    PERMISSIONS.POST_FGT_TEMPLATE_ADD_TEMPLATE,
  ]),
  path: FORM_SYS_ROUTES.createFormTemplate,
  loader: async ({ context: { queryClient } }) => {
    const data = await queryClient.ensureQueryData(getQueryCategoriesOptions());
    const categoryId = displayNameToIdBase(
      data,
      SEMATIC_CATEGORY.UN_CATEGORIZED,
    );
    const typeId = displayNameToIdBase(data, SEMATIC_CATEGORY.FORM);
    const specialtyId = displayNameToIdBase(
      data,
      SEMATIC_CATEGORY.NON_SPECIALIZED,
    );
    return { categoryId, typeId, specialtyId };
  },
  validateSearch: zodValidator(
    z.object({
      tempTemplateId: z.string().optional(),
    }),
  ),
}).lazy(() => import('./form-template').then(({ CreateRoute }) => CreateRoute));

const editFormTemplateRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  staticData: {
    parent: FORM_SYS_ROUTES.index,
  },
  beforeLoad: beforeLoadRouterGuard([
    PERMISSIONS.POST_FGT_TEMPLATE_SAVE_TEMPLATE,
  ]),
  path: FORM_SYS_ROUTES.editFormTemplate,
  validateSearch: zodValidator(
    z.object({
      from: z.string().optional(),
    }),
  ),
  loader: async ({ context: { queryClient }, params: { id } }) => {
    const template = await queryClient.ensureQueryData(
      getSuspenseQueryTemplateByIdOptions(id),
    );

    // prefetch file info
    const imageEntries = Object.entries(
      template.templateContent.widgets,
    ).filter(
      (entry): entry is [string, SetRequired<TemplateImageV1, 'fileId'>] => {
        const [, widget] = entry;
        return widget.type === 'image' && !!widget.fileId;
      },
    );
    await Promise.all(
      imageEntries.map(([, widget]) =>
        queryClient.ensureQueryData(getQueryFileInfoByIdOptions(widget.fileId)),
      ),
    );
    return template;
  },
}).lazy(() => import('./form-template').then(({ EditRoute }) => EditRoute));

const formTemplateManagementSearchParams = commonQueryParamsSchema.extend({
  keyword: z.string().optional().default(''),
  includeDisabled: z.boolean().optional().default(false),
  searched: z.boolean().optional().default(false),
});

export type FormTemplateManagementSearchParams = z.infer<
  typeof formTemplateManagementSearchParams
>;

const manageFormTemplateRoute = createRoute({
  getParentRoute,
  staticData: FORM_SYS_CHILD_STATIC_DATA,
  beforeLoad: beforeLoadRouterGuard([
    PERMISSIONS.GET_FGT_TEMPLATE_QUERY_PUBLISHED_TEMPLATE,
  ]),
  path: FORM_SYS_ROUTES.formTemplateManagement,
  validateSearch: zodValidator(formTemplateManagementSearchParams),
}).lazy(() => import('./form-template-management').then(({ Route }) => Route));

const formRuleRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  staticData: FORM_SYS_CHILD_STATIC_DATA,
  // TODO: Rule Permission ?
  beforeLoad: beforeLoadRouterGuard([]),
  path: FORM_SYS_ROUTES.formRule,
  loader: async ({ context }) => {
    const { queryClient } = context;

    const data = await queryClient.ensureQueryData(getEventListQueryOptions());
    return data;
  },
}).lazy(() => import('./form-rule').then(({ Route }) => Route));

const formApplicationSearchParams = z
  .object({
    mrn: z.string().optional(),
    encounterId: z.string().optional(),
    templateId: z.string().optional(),
    runtimeId: z.string().optional(),
    rowType: runtimeRowTypeSchema.optional(),
  })
  .refine((data) => !(data.templateId && data.runtimeId), {
    message: 'templateId and runtimeId are mutually exclusive',
  })
  .refine(
    (data) => {
      if (data.templateId || data.runtimeId) {
        return !!data.mrn && !!data.encounterId;
      }
      return true;
    },
    {
      message: 'templateId/runtimeId requires both mrn and encounterId',
    },
  )
  .refine((data) => !data.runtimeId || !!data.rowType, {
    message: 'runtimeId requires rowType',
  });

export type FormApplicationSearchParams = z.infer<
  typeof formApplicationSearchParams
>;

const formApplicationRoute = createRoute({
  getParentRoute,
  path: FORM_SYS_ROUTES.formApplication,
  staticData: {
    ...FORM_SYS_CHILD_STATIC_DATA,
    patientCardSource: SmartFormPatientCardSource,
  },
  validateSearch: zodValidator(formApplicationSearchParams),
}).lazy(() => import('./form-application').then(({ Route }) => Route));

const runtimeFormTodoRoute = createRoute({
  getParentRoute,
  path: FORM_SYS_ROUTES.runtimeFormTodo,
  staticData: FORM_SYS_CHILD_STATIC_DATA,
  validateSearch: zodValidator(
    z.object({
      mrn: z.string().optional(),
      encounterId: z.string().optional(),
    }),
  ),
}).lazy(() => import('./runtime-form-todo').then(({ Route }) => Route));

const customWidgetManagerRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  staticData: FORM_SYS_CHILD_STATIC_DATA,
  path: FORM_SYS_ROUTES.customWidgetManager,
  beforeLoad: beforeLoadRouterGuard([
    PERMISSIONS.GET_FGT_COMPONENT_QUERY_COMPONENT_BY_ID,
  ]),
  loader: async ({ context }) => {
    const { queryClient } = context;

    const data = await queryClient.ensureQueryData(
      getUserWidgetsQueryOptions(),
    );
    return data;
  },
}).lazy(() => import('./custom-widget-manager').then(({ Route }) => Route));

const formTemplateVersionRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  staticData: {
    parent: FORM_SYS_ROUTES.formTemplateManagement,
    home: FORM_SYS_ROUTES.index,
  },
  path: FORM_SYS_ROUTES.formTemplateVersion,
  beforeLoad: beforeLoadRouterGuard([
    PERMISSIONS.GET_FGT_TEMPLATE_QUERY_VERSION_CONTROL_BY_ID,
  ]),
}).lazy(() => import('./form-template-version').then(({ Route }) => Route));

export const formSysRoute = createRoute({
  getParentRoute,
  path: FORM_SYS_ROUTES.index,
  staticData: {
    parent: null,
    home: null,
  },
  beforeLoad: beforeLoadRouterGuard([
    PERMISSIONS.GET_FGT_TEMPLATE_QUERY_DRAFT_TEMPLATE,
    PERMISSIONS.GET_FGT_TEMPLATE_QUERY_TEMPLATE,
  ]),
  loader: async ({ context }) => {
    const { queryClient } = context;
    const typeId = await getTypeId(queryClient, SEMATIC_CATEGORY.FORM);

    await queryClient.ensureQueryData(
      getSuspenseQueryDraftTemplateOptions({
        typeId,
      }),
    );

    await queryClient.ensureQueryData(getEventListQueryOptions());
  },
})
  .lazy(() => import('./form-sys').then(({ Route }) => Route))
  .addChildren([
    createFormTemplateRoute,
    editFormTemplateRoute,
    manageFormTemplateRoute,
    formRuleRoute,
    formApplicationRoute,
    customWidgetManagerRoute,
    runtimeFormTodoRoute,
    formTemplateVersionRoute,
  ]);
